import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import ThemeToggle from "../../components/ThemeToggle";
import { Sparkle, LogOut, LayoutDashboard, Users, Map, ScrollText, TrendingUp, ArrowLeft } from "lucide-react";

const items = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "admin-nav-overview" },
    { to: "/admin/retention", label: "Retention", icon: TrendingUp, testid: "admin-nav-retention" },
    { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
    { to: "/admin/destinations", label: "Destinations", icon: Map, testid: "admin-nav-destinations" },
    { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testid: "admin-nav-audit" },
];

export default function AdminLayout({ children, title, subtitle }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const handleLogout = async () => { await logout(); navigate("/login"); };

    return (
        <div className="min-h-screen bg-sand-50 text-ink flex">
            <aside className="hidden lg:flex w-64 flex-col border-r border-ink/10 bg-sand-50/60 sticky top-0 h-screen">
                <Link to="/admin" className="flex items-center gap-3 px-6 h-20 border-b border-ink/10" data-testid="admin-brand">
                    <Sparkle strokeWidth={1.25} className="w-5 h-5 text-planet-sun" />
                    <span className="font-serif text-xl tracking-tight">Admin</span>
                </Link>
                <nav className="flex-1 px-4 py-6 space-y-1">
                    {items.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            data-testid={it.testid}
                            className={({ isActive }) =>
                                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors " +
                                (isActive
                                    ? "bg-ink/5 text-ink"
                                    : "text-ink-muted hover:text-ink hover:bg-ink/5")
                            }
                        >
                            <it.icon strokeWidth={1.5} className="w-4 h-4" />
                            {it.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="px-4 py-4 border-t border-ink/10 space-y-2">
                    <Link to="/dashboard" className="flex items-center gap-2 text-xs text-ink-muted hover:text-ink" data-testid="admin-back-to-app">
                        <ArrowLeft strokeWidth={1.5} className="w-3 h-3" /> Back to StarPath
                    </Link>
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-ink-muted truncate">{user?.email}</span>
                        <div className="flex items-center gap-1">
                            <ThemeToggle />
                            <button onClick={handleLogout} className="p-1.5 text-ink-muted hover:text-ink" data-testid="admin-logout" aria-label="Logout">
                                <LogOut strokeWidth={1.5} className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-sand-50/95 backdrop-blur border-b border-ink/10">
                <div className="flex items-center justify-between px-4 h-14">
                    <Link to="/admin" className="font-serif text-lg flex items-center gap-2">
                        <Sparkle strokeWidth={1.25} className="w-4 h-4 text-planet-sun" /> Admin
                    </Link>
                    <Link to="/dashboard" className="text-xs text-ink-muted">Back</Link>
                </div>
                <div className="flex items-center gap-4 px-4 pb-2 overflow-x-auto">
                    {items.map((it) => (
                        <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) =>
                            "text-xs whitespace-nowrap " + (isActive ? "text-ink" : "text-ink-muted")
                        } data-testid={`mobile-${it.testid}`}>
                            {it.label}
                        </NavLink>
                    ))}
                </div>
            </div>

            <main className="flex-1 lg:pt-0 pt-28 animate-fade-up">
                <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto">
                    {title && (
                        <div className="mb-8">
                            <div className="sp-overline text-ink-muted mb-2">Admin Control Center</div>
                            <h1 className="font-serif text-4xl tracking-tight" data-testid="admin-page-title">{title}</h1>
                            {subtitle && <p className="text-ink-muted mt-2">{subtitle}</p>}
                        </div>
                    )}
                    {children}
                </div>
            </main>
        </div>
    );
}
